import {
  enhanceFormFieldDataWithTypes,
  toPaginate,
  getBulkImportErrorData,
  getFiltersGlobalFilters,
} from '@/utils/infrastructure';
import { CoreEnums } from '@/utils/domain/constants';
import { VALIDATION_INTERNAL_ERRORS } from '@/utils/infrastructure/constants';
import _, { merge } from 'lodash';
import { getIntl } from 'umi';
import { bindServices } from '@/services/api';

const api = bindServices('users');

import {
  queryAddEnviroments,
  queryAddUserGroupUser,
  queryBulkAction,
  queryBulkTwoFactor,
  queryChangeUserEmail,
  queryConfirmAccount,
  queryGetEnvironmentsConfig,
  queryResetPassword,
  queryRestoreAndUpdateUsers,
  queryRestoreUser,
  querySendAccountConfirmationEmail,
  querySendRecoveryPasswordEmail,
  querySetDefaultUser,
  queryUnlockAccount,
  queryUserActivate,
  queryUserGroupUsers,
} from './service';

const { BulkActionType } = CoreEnums;

import { toImportExcel } from '@/services/global';
import CoreQueries from '@/utils/input/queries';
import { filtersConfig } from './utils';

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const fetchParams = 'User';
const userQueries = CoreQueries[fetchParams];

/**
 * Maps backend validation errors to form field names:
 * - `fullName` → both `names` and `lastNames`
 * - `extraData.<field>` → `<field>` (extra fields render by internalName)
 */
const mapUserValidationErrors = (validationErrors = []) => {
  const mapped = [];
  validationErrors.forEach((error) => {
    if (error?.field === 'fullName') {
      mapped.push({ ...error, field: 'names' }, { ...error, field: 'lastNames' });
      return;
    }
    if (typeof error?.field === 'string' && error.field.startsWith('extraData.')) {
      mapped.push({ ...error, field: error.field.replace('extraData.', '') });
      return;
    }
    mapped.push(error);
  });
  return mapped;
};

const toRestoreUserFromImportError = (record, importItemsById = {}) => {
  if (!record?.metadata?.id) return null;
  if (record?.data && typeof record.data === 'object' && !Array.isArray(record.data)) {
    return record.data;
  }
  return importItemsById[record.metadata.id] || null;
};

/**
 * Builds fetchList payload from global + users state (filters, pagination, search).
 * Shared by delete / restore / bulk / import refresh paths to avoid filter drift.
 */
const buildUsersListRefetchParams = (global = {}, users = {}) => {
  const { searchCriteria, isActive, pageSize, globalFilters } = global;
  const { pagination, orderBy, expand, select: selectFields, notSelectSearch } = users;

  const filtersSource =
    globalFilters?.users != null ? globalFilters : { users: globalFilters || {} };
  const globalFiltersStr = getFiltersGlobalFilters(filtersSource, 'users');

  let combinedFilter = null;
  if (isActive && globalFiltersStr) {
    combinedFilter = `${isActive} and ${globalFiltersStr}`;
  } else if (isActive) {
    combinedFilter = isActive;
  } else if (globalFiltersStr) {
    combinedFilter = globalFiltersStr;
  }

  return {
    ...pagination,
    page: pagination?.page ?? 1,
    ...(pageSize && { limit: pageSize }),
    ...(searchCriteria && { search: searchCriteria }),
    ...(notSelectSearch && { notSelectSearch }),
    ...(combinedFilter && { filter: combinedFilter }),
    ...(orderBy && { orderBy }),
    ...(expand && { expand }),
    ...(selectFields && { select: selectFields }),
  };
};

const initialState = {
  newUserGroup: false,
  newUserOnGroup: false,
  newUserOnEnvironment: false,
  // ConfigListPage / useConfigForm contract flags.
  showForm: false,
  detail: false,
  item: null,
  id: null,
  success: false,
  validationErrors: [],
  // Required by useConfigListController — must always be a stable object.
  activatingItemIds: {},
  // Loading map for two-factor switches (row + bulk).
  twoFactorUpdatingItemIds: {},
  // Spinner for row actions (confirm account, recovery email, unlock, etc.).
  rowActionLoadingItemIds: {},
  // Legacy flags kept in sync for backward compatibility.
  newUser: false,
  selectedItem: null,
  formConfig: null,
  list: [],
  userGroups: [],
  pagination: { ...meta },
  select: userQueries.paged.select,
  notSelectSearch: userQueries.paged.notSelectSearch,
  expand: userQueries.paged.expand,
  filtersConfig,
  view: false,
  orderBy: '',
  isConfig: false,
  userEnvironments: [],
  idSelected: null,
  openResetPasswordModal: false,
  changeEmailModal: null,
  showImport: false,
  bulkActionErrors: null,
  bulkActionErrorMessage: null,
  showBulkActionErrors: false,
  bulkActionErrorType: 'delete', // 'delete' o 'import'
  restoreUserModal: {
    visible: false,
    message: '',
    userIds: null,
    fromImportErrors: false,
  },
  // True after restoring from import-errors drawer; triggers list refresh on close.
  hadImportRestores: false,
  extraFields: [],
};

const Model = {
  namespace: 'users',

  state: { ...initialState },

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const params = {
          ...userQueries.paged,
          ...payload,
        };
        const response = yield call(api.getAll, {
          route: 'user/paged',
          params,
        });

        yield put({
          type: 'queryUsers',
          payload: {
            ...response,
            page: payload.page,
            limit: payload.limit,
          },
        });

        const extraFields = (yield select((state) => state.users.extraFields)) || [];
        if (extraFields.length > 0) return;

        try {
          const catalog = yield call(api.getAll, {
            route: 'userExtraField',
            params: {
              orderBy: 'order asc, displayText asc',
            },
          });
          const latestExtraFields = (yield select((state) => state.users.extraFields)) || [];
          if (latestExtraFields.length > 0) return;
          yield put({ type: 'setExtraFields', payload: catalog });
        } catch (extraFieldsError) {
          // Secondary request — ignore abort/errors so the paged list stays applied.
        }
      } catch (e) {}
    },

    // Effect to load extra fields independently
    // Useful to reload only extraFields without affecting the user list
    // Currently not used, but kept for future needs
    // *fetchExtraFields({ payload }, { call, put }) {
    //   try {
    //     const responseExtraFields = yield call(queryExtraFields, {
    //       orderBy: 'order asc, displayText asc',
    //     });
    //
    //     yield put({
    //       type: 'setExtraFields',
    //       payload: responseExtraFields,
    //     });
    //   } catch (e) {}
    // },

    *addNewUserGroup({ payload }, { put }) {
      yield put({ type: 'addUserGroup', payload });
    },

    *addNewUserOnGroup({ payload }, { put }) {
      yield put({ type: 'addUserOnGroup', payload });
    },

    *addNewUserOnEnvironment({ payload }, { put }) {
      yield put({ type: 'addUserOnEnvironment', payload });
    },

    // ===== ConfigListPage / useConfigForm contract =====

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
    },

    *fetchItem({ payload }, { call, put, select }) {
      try {
        const currentState = yield select((state) => state.users);
        const params = payload.detail
          ? userQueries.itemDetail
          : { ...userQueries.item, ...(payload?.params && { ...payload.params }) };
        const response = yield call(api.getById, {
          route: 'user',
          id: payload.id,
          params,
        });

        // Ensure extraFields are loaded before binding the form.
        let extraFields = currentState.extraFields;
        if (!extraFields || extraFields.length === 0) {
          extraFields = yield call(api.getAll, {
            route: 'userExtraField',
            params: { orderBy: 'order asc, displayText asc' },
          });
          yield put({ type: 'setExtraFields', payload: extraFields });
        }

        yield put({ type: 'queryItem', payload: response });
      } catch (error) {
        throw error;
      }
    },

    *fetchCreate({ payload }, { call, put }) {
      try {
        yield put({ type: 'changeSuccess', payload: { success: false } });
        yield call(api.create, { route: 'user', data: payload.data });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'changeSuccess', payload: { success: true } });
      } catch (error) {
        const validationErrors = error?.validationErrors || [];
        const itemDeletedError = validationErrors.find(
          (ve) =>
            ve.errorCode === VALIDATION_INTERNAL_ERRORS.VALIDATION_ITEM_DELETED_EXISTS &&
            ve.metadata?.id,
        );
        if (itemDeletedError) {
          const message =
            (itemDeletedError.errorMessages && itemDeletedError.errorMessages[0]) || error?.message;
          yield put({
            type: 'showRestoreUserModal',
            payload: { message, userId: itemDeletedError.metadata.id },
          });
          return;
        }
        const mappedErrors = mapUserValidationErrors(validationErrors);
        yield put({
          type: 'global/showNotificationsForm',
          payload: {
            visible: true,
            type: 'error',
            ...(error?.message && { message: error.message }),
          },
          validationErrors: mappedErrors,
        });
        yield put({
          type: 'setValidationErrors',
          payload: mappedErrors,
        });
      }
    },

    *fetchUpdate({ payload }, { call, put }) {
      try {
        yield put({ type: 'changeSuccess', payload: { success: false } });
        yield call(api.update, { route: 'user', data: payload.data });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'changeSuccess', payload: { success: true } });
      } catch (error) {
        const validationErrors = error?.validationErrors || [];
        const itemDeletedError = validationErrors.find(
          (ve) =>
            ve.errorCode === VALIDATION_INTERNAL_ERRORS.VALIDATION_ITEM_DELETED_EXISTS &&
            ve.metadata?.id,
        );
        if (itemDeletedError) {
          const message =
            (itemDeletedError.errorMessages && itemDeletedError.errorMessages[0]) || error?.message;
          yield put({
            type: 'showRestoreUserModal',
            payload: { message, userId: itemDeletedError.metadata.id },
          });
          return;
        }
        const mappedErrors = mapUserValidationErrors(validationErrors);
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: error.message },
            validationErrors: mappedErrors,
          });
        }
        yield put({
          type: 'setValidationErrors',
          payload: mappedErrors,
        });
      }
    },

    *fetchActivate({ payload }, { call, put }) {
      const { id } = payload;
      yield put({ type: 'queryActivatingItem', payload: { id, active: true } });
      try {
        yield call(queryUserActivate, id, payload.state);
        yield put({ type: 'userActive', payload });
      } catch (e) {
        throw e;
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { id, active: false } });
      }
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },

    *fetchUserOnGroup({ payload }, { call, put }) {
      try {
        const response = yield call(queryUserGroupUsers, payload);
        yield put({ type: 'listUserOnGroup', response });
      } catch (error) {
        // Re-throw the error so dva's automatic loading works correctly
        throw error;
      }
    },

    *hideNewUserOnGroup({ payload = 'hideUserOnGroup' }, { put }) {
      yield put({ type: payload });
    },

    *hideNewUserOnEnvironment({ payload = 'hideUserOnEnvironment' }, { put }) {
      yield put({ type: payload });
    },

    *onCloseUserOnGroup(_a, { put }) {
      yield put({ type: 'closeUserOnGroup' });
    },

    *onCloseUserOnEnvironment(_a, { put }) {
      yield put({ type: 'closeUserOnEnvironment' });
    },

    *addUserGroupUser({ payload }, { put }) {
      yield put({ type: 'addUserGroupList', payload });
    },

    *saveUserGroupUsers({ payload }, { call, put }) {
      try {
        yield call(queryAddUserGroupUser, payload);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({
          type: 'closeUserOnGroup',
        });
        // Manually reset userGroupId field cache after closing
        yield put({
          type: 'global/resetApiData',
          payload: ['userGroupId'],
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: false,
              visibleForm: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },

    *closeAllFormUser({ payload }, { put }) {
      yield put({ type: 'closeFormUser', payload });
    },

    *addNewUser({ payload }, { put }) {
      yield put({ type: 'addUser', payload });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrViewUser', payload });
      }
    },

    *fetchDelete({ payload }, { call, put, select }) {
      try {
        const { id } = payload;
        yield put({
          type: 'global/modalConfirm',
          payload: { state: false, id: null },
        });
        yield put({
          type: 'queryShowForm',
          payload: { state: false, id: null, item: null, detail: false },
        });
        yield call(api.remove, { route: 'user', id });
        yield put({ type: 'bulkUserDelete', payload: { userIds: [id] } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        const { global, users } = yield select((state) => state);
        yield put({
          type: 'fetchList',
          payload: buildUsersListRefetchParams(global, users),
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *search({ payload }, { put }) {
      yield put({ type: 'global/tableSearchState', payload });
    },

    *editItemUser({ payload }, { call, put, select }) {
      try {
        const response = yield call(api.getById, {
          route: 'user',
          id: payload.id,
          params: payload.params,
        });

        // Ensure extraFields are loaded
        const currentState = yield select((state) => state.users);
        let extraFields = currentState.extraFields;

        if (!extraFields || extraFields.length === 0) {
          const responseExtraFields = yield call(api.getAll, {
            route: 'userExtraField',
            params: {
              orderBy: 'order asc, displayText asc',
            },
          });
          extraFields = responseExtraFields;

          // Update extraFields in state
          yield put({
            type: 'setExtraFields',
            payload: extraFields,
          });
        }

        yield put({
          type: 'editUser',
          payload: { ...payload, item: response },
        });
      } catch (error) {
        // Re-throw the error so dva's automatic loading works correctly
        throw error;
      }
    },

    *saveNewUser({ payload }, { call, put }) {
      try {
        yield call(api.create, {
          route: 'user',
          data: payload.data,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        yield put({ type: 'saveUser' });
        yield put({ type: 'fetchList', payload: payload.params });
      } catch (error) {
        const validationErrors = error?.validationErrors || [];
        const itemDeletedError = validationErrors.find(
          (ve) =>
            ve.errorCode === VALIDATION_INTERNAL_ERRORS.VALIDATION_ITEM_DELETED_EXISTS &&
            ve.metadata?.id,
        );
        if (itemDeletedError) {
          const message =
            (itemDeletedError.errorMessages && itemDeletedError.errorMessages[0]) || error?.message;
          const userId = itemDeletedError.metadata.id;
          yield put({
            type: 'showRestoreUserModal',
            payload: { message, userId },
          });
          return;
        }
        if (error.message) {
          return yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: error.message },
            validationErrors,
          });
        }

        yield put({
          type: 'global/showNotificationsForm',
          payload: { visible: true, type: 'error' },
        });
      }
    },

    *restoreUser({ payload }, { call, put, select }) {
      try {
        const { userId } = payload;
        const ids = Array.isArray(userId) ? userId : [userId];
        yield call(queryRestoreUser, ids);
        const currentState = yield select((state) => state.users);
        const fromImportErrors = currentState.restoreUserModal?.fromImportErrors;
        yield put({ type: 'closeRestoreUserModal' });
        yield put({
          type: 'queryShowForm',
          payload: { state: false, id: null, item: null, detail: false },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        if (fromImportErrors) {
          yield put({ type: 'removeBulkActionErrorsByUserIds', payload: { userIds: ids } });
          yield put({ type: 'markHadImportRestores' });
        } else {
          const { global, users } = yield select((state) => state);
          yield put({
            type: 'fetchList',
            payload: buildUsersListRefetchParams(global, users),
          });
        }
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *restoreUsersFromImport({ payload }, { call, put, select }) {
      try {
        const { userIds } = payload;
        if (!Array.isArray(userIds) || userIds.length === 0) return;
        const usersState = yield select((state) => state.users);
        const importErrors = usersState.bulkActionErrorData?.errors || [];
        const selectedUsers = importErrors
          .filter((e) => userIds.includes(e?.metadata?.id))
          .map((e) => e.restoreItem)
          .filter((item) => item && typeof item === 'object');

        if (selectedUsers.length === 0) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: getIntl().formatMessage({ id: 'global.error.default' }),
            },
          });
          return;
        }

        yield call(queryRestoreAndUpdateUsers, selectedUsers);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'removeBulkActionErrorsByUserIds', payload: { userIds } });
        yield put({ type: 'markHadImportRestores' });
        yield put({ type: 'closeRestoreUserModal' });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *saveEditUser({ payload }, { call, put }) {
      try {
        yield call(api.update, {
          route: 'user',
          data: payload.data,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        yield put({ type: 'saveUser' });
        yield put({ type: 'fetchList', payload: payload.params });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },

    *deleteItem({ id, params, view }, { call, put }) {
      try {
        yield put({ type: `global/modalConfirm`, state: false, id: null });
        const response = yield call(api.apiRoute, {
          route: `user/${id}`,
          method: 'delete',
        });
        yield put({ type: 'deleteUser', payload: response });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error.message) {
          yield put({
            type: `global/showNotifications${view ? 'Form' : 'Global'}`,
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
              autoHide: view,
            },
          });
        }
      }
    },

    *deleteItemUserGroup({ id }, { put }) {
      yield put({ type: 'deleteUserGroup', payload: id });
      yield put({ type: `global/modalConfirm`, state: false, id: null });
    },

    *editActive({ payload }, { call, put }) {
      try {
        yield call(queryUserActivate, payload.id, payload.state);
        yield put({ type: 'userActive', payload });
      } catch (e) {
        // Re-throw the error so dva's automatic loading works correctly
        throw e;
      }
    },

    *bulkActivateUsers({ payload }, { call, put }) {
      const ids = payload?.ids || payload?.userIds || [];
      if (!ids.length) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(queryBulkAction, BulkActionType.Activate, ids);
        yield put({ type: 'bulkUserActive', payload: { userIds: ids, state: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *bulkDeactivateUsers({ payload }, { call, put }) {
      const ids = payload?.ids || payload?.userIds || [];
      if (!ids.length) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(queryBulkAction, BulkActionType.Deactivate, ids);
        yield put({ type: 'bulkUserActive', payload: { userIds: ids, state: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *bulkSetTwoFactor({ payload }, { call, put }) {
      const userIds = payload?.userIds || payload?.ids || [];
      const { state } = payload;
      if (!userIds.length) return;

      yield put({ type: 'queryTwoFactorUpdatingItem', payload: { ids: userIds, active: true } });
      try {
        yield call(queryBulkTwoFactor, state, userIds);
        yield put({
          type: 'bulkUserTwoFactor',
          payload: { userIds, state },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      } finally {
        yield put({ type: 'queryTwoFactorUpdatingItem', payload: { ids: userIds, active: false } });
      }
    },

    *setTwoFactor({ payload }, { call, put }) {
      const { id, state } = payload;
      if (id == null) return;
      yield put({ type: 'queryTwoFactorUpdatingItem', payload: { id, active: true } });
      try {
        yield call(queryBulkTwoFactor, state, [id]);
        yield put({
          type: 'bulkUserTwoFactor',
          payload: { userIds: [id], state },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      } finally {
        yield put({ type: 'queryTwoFactorUpdatingItem', payload: { id, active: false } });
      }
    },

    *bulkDeleteUsers({ payload }, { call, put, select }) {
      try {
        const userIds = payload?.ids || payload?.userIds || [];
        if (userIds.length === 0) return;

        // Get current user list to be able to map IDs to names
        const currentState = yield select((state) => state.users);
        const usersList = currentState.list || [];

        const response = yield call(queryBulkAction, BulkActionType.Delete, userIds);
        if (!response) return;

        const requested = response.requested ?? 0;
        const errors = Array.isArray(response.errors) ? response.errors : [];

        // Handle BulkActionResponse
        if (response.type === 3 && requested > 0) {
          const successCount = requested - errors.length;

          if (errors.length > 0) {
            // There are errors - show drawer with details
            const intl = getIntl();

            let errorMessage;
            if (successCount > 0) {
              // There are successfully deleted elements and errors
              const eliminated = intl.formatMessage({
                id:
                  successCount === 1
                    ? 'global.bulk.delete.eliminated.singular'
                    : 'global.bulk.delete.eliminated.plural',
              });
              const successElement = intl.formatMessage({
                id:
                  successCount === 1
                    ? 'global.bulk.delete.element.singular'
                    : 'global.bulk.delete.element.plural',
              });
              const totalElement = intl.formatMessage({
                id:
                  requested === 1
                    ? 'global.bulk.delete.element.singular'
                    : 'global.bulk.delete.element.plural',
              });
              const errorElement = intl.formatMessage({
                id:
                  errors.length === 1
                    ? 'global.bulk.delete.element.singular'
                    : 'global.bulk.delete.element.plural',
              });
              const couldNotBeDeleted = intl.formatMessage({
                id:
                  errors.length === 1
                    ? 'global.bulk.delete.could.not.be.deleted.singular'
                    : 'global.bulk.delete.could.not.be.deleted.plural',
              });

              errorMessage = intl.formatMessage(
                { id: 'global.bulk.delete.success' },
                {
                  eliminated,
                  successCount,
                  successElement,
                  total: requested,
                  totalElement,
                  errorCount: errors.length,
                  errorElement,
                  couldNotBeDeleted,
                },
              );
            } else {
              // Only errors, no elements deleted
              const errorElement = intl.formatMessage({
                id:
                  errors.length === 1
                    ? 'global.bulk.delete.element.singular'
                    : 'global.bulk.delete.element.plural',
              });
              const couldNotBeDeleted = intl.formatMessage({
                id:
                  errors.length === 1
                    ? 'global.bulk.delete.could.not.be.deleted.singular'
                    : 'global.bulk.delete.could.not.be.deleted.plural',
              });

              errorMessage = intl.formatMessage(
                { id: 'global.bulk.delete.error.only' },
                {
                  errorCount: errors.length,
                  errorElement,
                  couldNotBeDeleted,
                },
              );
            }

            // Delete only users that had no errors
            const errorIds = errors.map((error) => error.id);
            const successfulUserIds = userIds.filter((id) => !errorIds.includes(id));

            // Only remove from list if there were successful users
            if (successfulUserIds.length > 0) {
              yield put({ type: 'bulkUserDelete', payload: { userIds: successfulUserIds } });
            }

            // Map errors with user names
            const errorsWithUserNames = errors.map((error) => {
              const user = usersList.find((u) => u.id === error.id);
              return {
                id: error.id,
                userName: user
                  ? user.fullName || user.names || `Usuario ${error.id}`
                  : `Usuario ${error.id}`,
                errorMessage: error.errorMessage,
              };
            });

            yield put({
              type: 'setBulkActionErrors',
              payload: {
                errorData: {
                  errorMessage,
                  errors: errorsWithUserNames,
                  errorType: 'delete',
                },
              },
            });

            // Automatically open the errors drawer
            yield put({
              type: 'toggleBulkActionErrorsDrawer',
              payload: { open: true },
            });
          } else {
            // Everything successful
            yield put({ type: 'bulkUserDelete', payload: { userIds } });
            yield put({
              type: 'global/showNotificationsGlobal',
              payload: { visible: true, type: 'success' },
            });
          }

          const { global, users } = yield select((state) => state);
          yield put({
            type: 'fetchList',
            payload: buildUsersListRefetchParams(global, users),
          });
        }
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'orderBy', payload });
    },

    *eventSorterUserGroup({ payload }, { put }) {
      yield put({ type: 'sorterUserGroup', payload });
    },

    *closeForms({ payload = 'closeForm' }, { put }) {
      yield put({ type: payload });
    },

    *hideAllForms({ payload = 'hideForms' }, { put }) {
      yield put({ type: payload });
    },

    *fetchUserOnEnvironment({ payload }, { call, put }) {
      try {
        const response = yield call(queryGetEnvironmentsConfig, payload);
        yield put({ type: 'listUserOnEnvironment', response });
      } catch (error) {
        // Re-throw the error so dva's automatic loading works correctly
        throw error;
      }
    },

    *addListEnviroment({ payload }, { put }) {
      yield put({ type: 'addEnviromentToList', payload });
    },

    *deleteEnviromentFromList({ payload }, { put }) {
      yield put({ type: 'removeEnviromentToList', payload });
      yield put({ type: `global/modalConfirmDrawer`, state: false, id: null });
    },

    *saveNewListEnviroments({ payload }, { call, put }) {
      try {
        yield call(queryAddEnviroments, payload);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({
          type: 'closeUserOnEnvironment',
        });
        // Manually reset enviromentId field cache after closing
        yield put({
          type: 'global/resetApiData',
          payload: ['enviromentId'],
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: false,
              visibleForm: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },

    *fetchImport({ payload }, { call, put, select }) {
      try {
        const { file, isDeleteData } = payload;

        const { global, users } = yield select((state) => state);

        const res = yield call(toImportExcel, {
          route: 'user',
          fileContent: file,
          deleteExistingData: isDeleteData,
        });

        const errorData = getBulkImportErrorData(res, getIntl);
        const importItemsById = Array.isArray(res?.data?.items)
          ? res.data.items.reduce((acc, item) => {
              if (item?.id) acc[item.id] = item;
              return acc;
            }, {})
          : {};
        const hasImportedRows = res.importedRowsCount && res.importedRowsCount > 0;

        if (errorData) {
          const enrichedErrors = (errorData.errors || []).map((record) => {
            const restoreItem = toRestoreUserFromImportError(record, importItemsById);
            return restoreItem ? { ...record, restoreItem } : record;
          });
          yield put({
            type: 'setBulkActionErrors',
            payload: {
              errorData: {
                ...errorData,
                errors: enrichedErrors,
                importedRowsCount: res.importedRowsCount ?? 0,
              },
            },
          });
          yield put({
            type: 'toggleBulkActionErrorsDrawer',
            payload: { open: true },
          });
        }

        yield put({ type: 'queryImport', payload: { ...res } });

        if (hasImportedRows && !errorData) {
          yield put({
            type: 'fetchList',
            payload: buildUsersListRefetchParams(global, users),
          });
        }

        // Show success notification only if no errors
        if (!errorData) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'success',
            },
          });
        }
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: false,
              visibleForm: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },

    *closeForm({ payload }, { put }) {
      yield put({
        type: 'closeFormReducer',
        payload,
      });
    },

    *sendAccountConfirmationEmail({ payload }, { call, put }) {
      const { id } = payload || {};
      if (id == null) return;
      yield put({ type: 'queryRowActionLoading', payload: { id, active: true } });
      try {
        yield call(querySendAccountConfirmationEmail, id);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
          },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: `global/showNotificationsGlobal`,
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
              autoHide: false,
            },
          });
        }
      } finally {
        yield put({ type: 'queryRowActionLoading', payload: { id, active: false } });
      }
    },

    *confirmAccount({ payload }, { call, put }) {
      const { id } = payload || {};
      if (id == null) return;
      yield put({ type: 'queryRowActionLoading', payload: { id, active: true } });
      try {
        yield call(queryConfirmAccount, id);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
          },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: `global/showNotificationsGlobal`,
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
              autoHide: false,
            },
          });
        }
      } finally {
        yield put({ type: 'queryRowActionLoading', payload: { id, active: false } });
      }
    },

    *sendRecoveryPasswordEmail({ payload }, { call, put }) {
      const { id } = payload || {};
      if (id == null) return;
      yield put({ type: 'queryRowActionLoading', payload: { id, active: true } });
      try {
        yield call(querySendRecoveryPasswordEmail, id);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
          },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: `global/showNotificationsGlobal`,
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
              autoHide: false,
            },
          });
        }
      } finally {
        yield put({ type: 'queryRowActionLoading', payload: { id, active: false } });
      }
    },

    *unlockAccount({ payload }, { call, put }) {
      const { id } = payload || {};
      if (id == null) return;
      yield put({ type: 'queryRowActionLoading', payload: { id, active: true } });
      try {
        yield call(queryUnlockAccount, id);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
          },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: `global/showNotificationsGlobal`,
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
              autoHide: false,
            },
          });
        }
      } finally {
        yield put({ type: 'queryRowActionLoading', payload: { id, active: false } });
      }
    },

    *setDefaultUser({ payload }, { call, put }) {
      const { id, userId } = payload || {};
      if (id == null || userId == null) return;
      yield put({ type: 'queryRowActionLoading', payload: { id, active: true } });
      try {
        yield call(querySetDefaultUser, userId);
        yield put({ type: 'userSetDefault', payload: { id, userId } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
          },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: `global/showNotificationsGlobal`,
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
              autoHide: false,
            },
          });
        }
      } finally {
        yield put({ type: 'queryRowActionLoading', payload: { id, active: false } });
      }
    },

    *toggleResetPasswordModal({ payload }, { put }) {
      yield put({
        type: 'queryToggleResetPasswordModal',
        payload,
      });
    },

    *changeEmail({ payload }, { call, put, select }) {
      const { id, email } = payload || {};
      if (id == null || !email) return;
      try {
        yield call(queryChangeUserEmail, { id, email });
        yield put({ type: 'closeChangeEmailModal' });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        const { global, users } = yield select((state) => state);
        yield put({
          type: 'fetchList',
          payload: buildUsersListRefetchParams(global, users),
        });
        if (users.showForm && users.detail && users.id === id) {
          yield put({ type: 'fetchItem', payload: { id, detail: true } });
        }
      } catch (error) {
        yield put({ type: 'closeChangeEmailModal' });
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
              autoHide: false,
            },
          });
        }
      }
    },

    *resetPassword({ payload }, { call, put }) {
      try {
        yield call(queryResetPassword, payload);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
          },
        });
        yield put({
          type: 'queryToggleResetPasswordModal',
          payload: {
            open: false,
            id: null,
          },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: `global/showNotificationsGlobal`,
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
              autoHide: false,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },
    *toggleImportComponent({ payload }, { put }) {
      yield put({
        type: 'queryToggleImportComponent',
        payload: { showImport: payload.show },
      });
    },

    *toggleBulkActionErrorsDrawer({ payload }, { put, select }) {
      if (payload.open === false) {
        const usersState = yield select((state) => state.users);
        const bulkErr = usersState.bulkActionErrorData;
        const importHadSuccessfulRows =
          bulkErr?.errorType === 'import' && Number(bulkErr.importedRowsCount) > 0;
        if (usersState.hadImportRestores || importHadSuccessfulRows) {
          const { global, users } = yield select((state) => state);
          yield put({
            type: 'fetchList',
            payload: buildUsersListRefetchParams(global, users),
          });
          yield put({ type: 'clearHadImportRestores' });
        }
      }
      yield put({
        type: 'queryToggleBulkActionErrorsDrawer',
        payload,
      });
    },

    *clearBulkActionErrors({ payload }, { put }) {
      yield put({
        type: 'queryClearBulkActionErrors',
      });
    },
  },

  reducers: {
    queryGetConfig(state, { payload }) {
      return {
        ...state,
        isConfig: true,
        newUserOnGroup: false,
        idSelected: payload.idSelected,
        formConfig: payload.form,
        userEnvironments: payload.data.map((item) => ({
          id: item.environment.id,
          name: item.environment.name,
        })),
      };
    },
    hideConfig(state) {
      return {
        ...state,
        selectedItem: null,
        processConfigList: [],
        userEnvironments: [],
        isConfig: false,
        formConfig: null,
      };
    },
    addEnviromentToList(state, { payload }) {
      return {
        ...state,
        userEnvironments: [...state.userEnvironments, payload],
      };
    },
    removeEnviromentToList(state, { payload }) {
      return {
        ...state,
        userEnvironments: state.userEnvironments.filter((item) => item.id !== payload),
      };
    },

    queryUsers(state, { payload }) {
      return {
        ...state,
        list: payload.items,
        pagination: merge(state.pagination, toPaginate(payload)),
      };
    },

    setExtraFields(state, { payload }) {
      return {
        ...state,
        extraFields: payload,
        // The shared list controller rebuilds columns when `list` changes.
        // The descriptor's buildColumns stays stable and reads the catalog the page published.
        list: Array.isArray(state.list) ? [...state.list] : state.list,
      };
    },

    // ===== ConfigListPage / useConfigForm contract reducers =====

    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        detail: payload.detail ?? false,
        id: payload.id ?? null,
        item: payload.item ?? null,
        success: false,
        validationErrors: [],
        // Keep legacy flags in sync.
        newUser: payload.state,
        selectedItem: payload.item ?? null,
        view: payload.detail ?? false,
      };
    },

    queryItem(state, { payload }) {
      return {
        ...state,
        item: payload,
        selectedItem: payload,
      };
    },

    success(state, { payload }) {
      return {
        ...state,
        success: payload?.success ?? payload,
        validationErrors: [],
      };
    },

    setValidationErrors(state, { payload }) {
      return {
        ...state,
        validationErrors: payload || [],
      };
    },

    queryActivatingItem(state, { payload }) {
      const { id, ids, active } = payload || {};
      const activatingItemIds = { ...(state.activatingItemIds || {}) };

      if (Array.isArray(ids) && ids.length) {
        ids.forEach((itemId) => {
          if (itemId == null) return;
          if (active) {
            activatingItemIds[itemId] = true;
          } else {
            delete activatingItemIds[itemId];
          }
        });
        return { ...state, activatingItemIds };
      }

      if (id == null) {
        return { ...state, activatingItemIds: {} };
      }

      if (active) {
        activatingItemIds[id] = true;
      } else {
        delete activatingItemIds[id];
      }
      return { ...state, activatingItemIds };
    },

    queryTwoFactorUpdatingItem(state, { payload }) {
      const { id, ids, active } = payload || {};
      const twoFactorUpdatingItemIds = { ...(state.twoFactorUpdatingItemIds || {}) };

      if (Array.isArray(ids) && ids.length) {
        ids.forEach((itemId) => {
          if (itemId == null) return;
          if (active) {
            twoFactorUpdatingItemIds[itemId] = true;
          } else {
            delete twoFactorUpdatingItemIds[itemId];
          }
        });
        return { ...state, twoFactorUpdatingItemIds };
      }

      if (id == null) {
        return { ...state, twoFactorUpdatingItemIds: {} };
      }

      if (active) {
        twoFactorUpdatingItemIds[id] = true;
      } else {
        delete twoFactorUpdatingItemIds[id];
      }
      return { ...state, twoFactorUpdatingItemIds };
    },

    queryRowActionLoading(state, { payload }) {
      const { id, active } = payload || {};
      const rowActionLoadingItemIds = { ...(state.rowActionLoadingItemIds || {}) };

      if (id == null) {
        return { ...state, rowActionLoadingItemIds: {} };
      }

      if (active) {
        rowActionLoadingItemIds[id] = true;
      } else {
        delete rowActionLoadingItemIds[id];
      }
      return { ...state, rowActionLoadingItemIds };
    },

    userSetDefault(state, { payload }) {
      const { id, userId } = payload || {};
      const list = state.list.map((item) => {
        if (item.id === id) return { ...item, isDefault: true };
        if (userId != null && item.userId === userId) return { ...item, isDefault: false };
        return item;
      });
      return { ...state, list };
    },

    reset() {
      return {
        ...initialState,
        pagination: { ...meta },
        activatingItemIds: {},
        twoFactorUpdatingItemIds: {},
        rowActionLoadingItemIds: {},
      };
    },

    addUserGroup(state, { payload }) {
      return {
        ...state,
        newUserGroup: payload.state,
        selectedItem: payload.item,
        view: payload.view,
      };
    },

    addUserOnGroup(state, { payload }) {
      return {
        ...state,
        newUserOnGroup: payload.state,
        selectedItem: payload.item,
        formConfig: payload.form,
      };
    },

    addUserOnEnvironment(state, { payload }) {
      return {
        ...state,
        newUserOnEnvironment: payload.state,
        selectedItem: payload.item,
        formConfig: payload.form,
      };
    },

    sorterUserGroup(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    hideUserOnGroup(state) {
      return {
        ...state,
        selectedItem: null,
        userGroups: [],
        formConfig: null,
      };
    },

    hideUserOnEnvironment(state) {
      return {
        ...state,
        selectedItem: null,
        userEnvironments: [],
        formConfig: null,
      };
    },

    closeUserOnGroup(state) {
      return {
        ...state,
        newUserOnGroup: false,
      };
    },

    closeUserOnEnvironment(state) {
      return {
        ...state,
        newUserOnEnvironment: false,
      };
    },

    listUserOnGroup(state, { response }) {
      return {
        ...state,
        userGroups: response.map((item) => {
          return {
            id: item.id,
            group: item.name,
          };
        }),
      };
    },

    listUserOnEnvironment(state, { response }) {
      return {
        ...state,
        userEnvironments: response.map((item) => {
          return {
            id: item.id,
            name: item.name,
          };
        }),
      };
    },

    addUserGroupList(state, { payload }) {
      return {
        ...state,
        userGroups: [...state.userGroups, payload],
      };
    },

    addUser(state, { payload }) {
      return {
        ...state,
        newUser: payload.state,
        selectedItem: payload.item,
        view: payload.view,
        id: payload.id,
        newUserOnGroup: false,
      };
    },

    editOrViewUser(state) {
      return {
        ...state,
        selectedItem: {},
      };
    },

    editUser(state, { payload }) {
      return {
        ...state,
        selectedItem: payload.item,
        view: payload.view,
        id: payload.id,
        newUserOnGroup: false,
      };
    },

    closeFormUser(state) {
      return {
        ...state,
        newUser: false,
        selectedItem: null,
      };
    },

    saveUser(state) {
      return {
        ...state,
        newUser: false,
        selectedItem: null,
        id: null,
      };
    },

    deleteUser(state) {
      return {
        ...state,
      };
    },

    deleteUserGroup(state, { payload }) {
      return {
        ...state,
        userGroups: state.userGroups.filter((item) => item.id !== payload),
      };
    },

    userActive(state, { payload }) {
      return {
        ...state,
        list: _.map(state.list, (o) => {
          if (o.id === payload.id) {
            _.set(o, 'isActive', payload.state);
          }
          return o;
        }),
      };
    },

    bulkUserActive(state, { payload }) {
      return {
        ...state,
        list: _.map(state.list, (o) => {
          if (payload.userIds.includes(o.id)) {
            _.set(o, 'isActive', payload.state);
          }
          return o;
        }),
      };
    },

    bulkUserTwoFactor(state, { payload }) {
      return {
        ...state,
        list: _.map(state.list, (o) => {
          if (!payload.userIds.includes(o.id)) return o;
          if (o.user) {
            return { ...o, user: { ...o.user, twoFactorEnabled: payload.state } };
          }
          return { ...o, twoFactorEnabled: payload.state };
        }),
      };
    },

    bulkUserDelete(state, { payload }) {
      return {
        ...state,
        list: state.list.filter((o) => !payload.userIds.includes(o.id)),
      };
    },

    orderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    hideForms(state) {
      return {
        ...state,
        newUserGroup: false,
        newUserOnGroup: false,
        newUserOnEnvironment: false,
        newUser: false,
        selectedItem: null,
        view: false,
        extraFields: [], // Clear extraFields when leaving the section
      };
    },

    queryToggleImportComponent(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },

    queryImport(state) {
      return {
        ...state,
        showImport: false,
      };
    },

    queryToggleResetPasswordModal(state, { payload }) {
      return {
        ...state,
        openResetPasswordModal: payload.open,
        idSelected: payload.id,
      };
    },

    openChangeEmailModal(state, { payload }) {
      return {
        ...state,
        changeEmailModal: payload,
      };
    },

    closeChangeEmailModal(state) {
      if (!state.changeEmailModal) return state;
      return {
        ...state,
        changeEmailModal: null,
      };
    },

    setBulkActionErrors(state, { payload }) {
      return {
        ...state,
        bulkActionErrorData: payload.errorData,
        showBulkActionErrors: true,
      };
    },

    queryToggleBulkActionErrorsDrawer(state, { payload }) {
      return {
        ...state,
        showBulkActionErrors: payload.open,
      };
    },

    showRestoreUserModal(state, { payload }) {
      const userIds = payload?.userIds ?? (payload?.userId != null ? [payload.userId] : null);
      return {
        ...state,
        restoreUserModal: {
          visible: true,
          message: payload?.message || '',
          userIds,
          fromImportErrors: payload?.fromImportErrors ?? false,
        },
      };
    },

    closeRestoreUserModal(state) {
      return {
        ...state,
        restoreUserModal: {
          visible: false,
          message: '',
          userIds: null,
          fromImportErrors: false,
        },
      };
    },

    markHadImportRestores(state) {
      return state.hadImportRestores ? state : { ...state, hadImportRestores: true };
    },

    /**
     * After a successful restore from the import-errors drawer, drop those rows —
     * they are no longer unresolved errors. Main list refresh stays on drawer close.
     */
    removeBulkActionErrorsByUserIds(state, { payload }) {
      const userIds = payload?.userIds;
      if (!Array.isArray(userIds) || userIds.length === 0) return state;
      const idSet = new Set(userIds.filter(Boolean));
      const errors = state.bulkActionErrorData?.errors;
      if (!Array.isArray(errors) || errors.length === 0) return state;

      const nextErrors = errors.filter((entry) => !idSet.has(entry?.metadata?.id));
      if (nextErrors.length === errors.length) return state;

      return {
        ...state,
        bulkActionErrorData: {
          ...state.bulkActionErrorData,
          errors: nextErrors,
        },
      };
    },

    clearHadImportRestores(state) {
      return state.hadImportRestores ? { ...state, hadImportRestores: false } : state;
    },

    queryClearBulkActionErrors(state) {
      return {
        ...state,
        bulkActionErrorData: null,
        showBulkActionErrors: false,
        hadImportRestores: false,
      };
    },
  },
};

export default Model;
